/**
 * ObligationsTab - Active Obligations table + FOIR Impact Simulator
 */

import React, { useMemo } from 'react';
import { Table, type TableColumn } from 'equal-ds-ui';
import type { ObligationsTabProps, EnrichedEmiWindow } from '../../../../types/lending-report.types';
import { formatINR } from '../../../../types/lending-report.types';

const STATUS_BADGE: Record<EnrichedEmiWindow['status'], string> = {
  Regular: 'bg-green-100 text-green-700',
  Delayed: 'bg-red-100 text-red-700',
  Unknown: 'bg-gray-100 text-gray-500',
};

interface FOIRCard {
  newEMI: number;
  projectedFOIR: number;
}

const FOIR_PRESETS = [5000, 8000, 12000];

const getFOIRRisk = (foir: number): { label: string; borderColor: string; textColor: string; bgColor: string } => {
  if (foir < 40) return { label: 'Safe', borderColor: '#4ade80', textColor: '#15803d', bgColor: '#f0fdf4' };
  if (foir <= 50) return { label: 'Moderate', borderColor: '#fbbf24', textColor: '#92400e', bgColor: '#fffbeb' };
  return { label: 'Risky', borderColor: '#f87171', textColor: '#991b1b', bgColor: '#fef2f2' };
};

export const ObligationsTab: React.FC<ObligationsTabProps> = ({ report }) => {
  const obligations: Partial<EnrichedEmiWindow>[] = useMemo(
    () =>
      report.enrichedEmiWindows && report.enrichedEmiWindows.length > 0
        ? report.enrichedEmiWindows
        : report.emiWindows,
    [report.enrichedEmiWindows, report.emiWindows],
  );

  const columns: TableColumn<Partial<EnrichedEmiWindow>>[] = useMemo(
    () => [
      {
        key: 'counterparty',
        header: 'OBLIGATION',
        width: 200,
        accessor: (row) => row.counterparty || '-',
      },
      {
        key: 'averageAmount',
        header: 'EMI / MONTH',
        width: 130,
        accessor: (row) => formatINR(row.averageAmount ?? 0),
      },
      {
        key: 'monthsLeft',
        header: 'MONTHS LEFT',
        width: 110,
        render: (_value, row) => row.monthsLeft != null ? String(row.monthsLeft) : '-',
      },
      {
        key: 'status',
        header: 'STATUS',
        width: 110,
        render: (_value, row) => {
          const status = row.status || 'Unknown';
          return (
            <span
              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[status] ?? STATUS_BADGE.Unknown}`}
            >
              {status}
            </span>
          );
        },
      },
      {
        key: 'percentOfIncome',
        header: '% OF INCOME',
        width: 110,
        render: (_value, row) => row.percentOfIncome != null ? `${row.percentOfIncome.toFixed(1)}%` : '-',
      },
    ],
    [],
  );

  // FOIR Simulator computation
  const currentTotalEMI = useMemo(
    () => report.emiWindows.reduce((sum, w) => sum + w.averageAmount, 0),
    [report.emiWindows],
  );

  const netMonthlyIncome = report.netMonthlyIncome;

  const foirCards: FOIRCard[] = useMemo(
    () =>
      FOIR_PRESETS.map((newEMI) => ({
        newEMI,
        projectedFOIR:
          netMonthlyIncome > 0
            ? ((currentTotalEMI + newEMI) / netMonthlyIncome) * 100
            : 0,
      })),
    [currentTotalEMI, netMonthlyIncome],
  );

  return (
    <div className="space-y-6">
      {/* Active Obligations Table */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Active Obligations ({obligations.length})
        </h3>
        {obligations.length > 0 ? (
          <div className="equal-ds-root">
            <Table<Partial<EnrichedEmiWindow>>
              className="shadow-sm rounded-xl"
              data={obligations}
              columns={columns}
            />
          </div>
        ) : (
          <div className="text-gray-400 text-sm text-center py-6">No obligation data available</div>
        )}
      </div>

      {/* FOIR Impact Simulator */}
      <div>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>FOIR Impact Simulator</h3>
        <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '16px' }}>What happens if a new EMI is added?</p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          {foirCards.map((card) => {
            const risk = getFOIRRisk(card.projectedFOIR);
            return (
              <div
                key={card.newEMI}
                style={{
                  borderRadius: '12px',
                  border: `2px solid ${risk.borderColor}`,
                  backgroundColor: risk.bgColor,
                  padding: '16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
              >
                <p style={{ fontSize: '12px', color: risk.textColor, marginBottom: '8px' }}>
                  New EMI: {formatINR(card.newEMI)}
                </p>
                <p style={{ fontSize: '28px', fontWeight: 700, color: risk.textColor, lineHeight: 1.2 }}>
                  {card.projectedFOIR.toFixed(1)}%
                </p>
                <p style={{ fontSize: '12px', fontWeight: 600, color: risk.textColor, marginTop: '4px' }}>
                  {risk.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
